// MQTT over WebSocket — the React equivalent of the Flutter mqtt_controller.dart.
//
// The Flutter app uses MqttServerClient over TCP 1883. A browser / Capacitor
// webview cannot open raw TCP, so this connects over WebSocket (mqtt.js). The
// broker must expose a WS listener; set REACT_APP_MQTT_WS_URL accordingly.
//
// Publish (command): topic `<uno>/device`, payload `[<uno>]<message>#<userId>`
// Subscribe (status): topic `<uno>/client`, status is the substring between ] and #

import mqtt from 'mqtt';

const WS_URL =
  process.env.REACT_APP_MQTT_WS_URL || 'ws://smartifyindia.co.in:9001/mqtt';

let client = null;
const listeners = new Set(); // (topic, payloadString) => void
const subscribed = new Map(); // topic -> active subscriber count (ref-count)

export function connectMqtt() {
  if (client && client.connected) return client;
  if (client) return client; // connecting in progress

  const clientId = `react_client_${Date.now()}`;
  client = mqtt.connect(WS_URL, {
    clientId,
    keepalive: 60,
    clean: true,
    reconnectPeriod: 2000,
    connectTimeout: 8000,
  });

  client.on('connect', () => {
    // Re-subscribe everything after a reconnect.
    subscribed.forEach((_count, t) => client.subscribe(t));
    // eslint-disable-next-line no-console
    console.log('[MQTT] connected', WS_URL);
  });
  client.on('message', (topic, message) => {
    const payload = message.toString();
    listeners.forEach((fn) => {
      try {
        fn(topic, payload);
      } catch (e) {
        /* ignore listener error */
      }
    });
  });
  client.on('error', (e) => console.error('[MQTT] error', e?.message || e));
  return client;
}

export function isConnected() {
  return !!client && client.connected;
}

// Tear down the connection (e.g. on logout) so a new session starts fresh.
export function disconnectMqtt() {
  if (client) {
    try { client.end(true); } catch (e) { /* ignore */ }
    client = null;
  }
  subscribed.clear();
}

export function subscribeTopic(topic) {
  connectMqtt();
  const count = subscribed.get(topic) || 0;
  subscribed.set(topic, count + 1);
  // Only hit the broker on the first subscriber for this topic.
  if (count === 0 && client && client.connected) client.subscribe(topic);
}

export function unsubscribeTopic(topic) {
  const count = subscribed.get(topic) || 0;
  if (count <= 1) {
    subscribed.delete(topic);
    if (client && client.connected) client.unsubscribe(topic);
  } else {
    subscribed.set(topic, count - 1);
  }
}

export function publishMessage(topic, message) {
  connectMqtt();
  if (client) client.publish(topic, message, { qos: 1 });
}

// Subscribe to incoming messages. Returns an unsubscribe function.
export function addMessageListener(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Parse the status substring between ] and # from a `<uno>/client` payload.
export function parseStatus(payload) {
  if (typeof payload !== 'string') return '';
  const start = payload.indexOf(']');
  const end = payload.indexOf('#');
  if (start === -1) return payload;
  return payload.substring(start + 1, end === -1 ? undefined : end);
}
